// src/components/CounselorFactorControl.js
import React, { useState } from 'react';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FACTOR_DATA } from '../data/factorInterpretations';
import TeachingPresentation from './TeachingPresentation';
import PrayerPresentation from './PrayerPresentation';
import { 
  Lock, 
  Unlock, 
  Eye, 
  EyeOff, 
  ChevronDown, 
  ChevronUp, 
  Book, 
  Heart,
  FileText,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const CounselorFactorControl = ({ couple, coupleDocId, partner1Progress, partner2Progress, onUpdate }) => {
  const [loading, setLoading] = useState({});
  const [message, setMessage] = useState('');
  const [expandedFactor, setExpandedFactor] = useState(null);
  const [factorAnswers, setFactorAnswers] = useState({});
  const [showTeaching, setShowTeaching] = useState(false);
  const [showPrayer, setShowPrayer] = useState(false);
  const [selectedFactorForPresentation, setSelectedFactorForPresentation] = useState(null);
  
  // Modal state
  const [selectedFactor, setSelectedFactor] = useState(null);
  const [activeTab, setActiveTab] = useState('questions');
  const [currentSlide, setCurrentSlide] = useState(0);

  // FIXED: Get the correct document ID - prioritize the prop passed from parent
  const getDocumentId = () => {
    const docId = coupleDocId || couple?.docId || couple?.id || couple?.coupleId;
    console.log('🔍 Document ID resolution:', { coupleDocId, coupleDocIdProp: couple?.docId, coupleId: couple?.id, coupleCoupleId: couple?.coupleId, resolved: docId });
    return docId;
  };

  // Helper function to get answer label
  const getAnswerLabel = (value, question) => {
    // NEW: Handle custom options array (from updated factorInterpretations.js)
    if (question.options && Array.isArray(question.options)) {
      const option = question.options.find(opt => opt.value === value);
      return option ? option.text : `Option ${value}`;
    }
    
    // Handle legacy answerOptions object
    if (question.answerOptions) {
      return question.answerOptions[value] || `Option ${value}`;
    }
    
    if (question.answerFormat) {
      const formats = {
        'frequency': { 1: 'Never', 2: 'Rarely', 3: 'Sometimes', 4: 'Often', 5: 'Always' },
        'frequency_reversed': { 1: 'Always', 2: 'Often', 3: 'Sometimes', 4: 'Rarely', 5: 'Never' },
        'agreement': { 1: 'Strongly Disagree', 2: 'Disagree', 3: 'Neutral', 4: 'Agree', 5: 'Strongly Agree' },
        'quality': { 1: 'Very Poor', 2: 'Poor', 3: 'Fair', 4: 'Good', 5: 'Excellent' },
        'satisfaction': { 1: 'Very Dissatisfied', 2: 'Dissatisfied', 3: 'Neutral', 4: 'Satisfied', 5: 'Very Satisfied' },
        'comfort': { 1: 'Very Uncomfortable', 2: 'Uncomfortable', 3: 'Neutral', 4: 'Comfortable', 5: 'Very Comfortable' },
        'confidence': { 1: 'Not at all Confident', 2: 'Slightly Confident', 3: 'Moderately Confident', 4: 'Very Confident', 5: 'Extremely Confident' },
        'ease': { 1: 'Very Difficult', 2: 'Difficult', 3: 'Moderate', 4: 'Easy', 5: 'Very Easy' },
        'importance': { 1: 'Not Important', 2: 'Slightly Important', 3: 'Moderately Important', 4: 'Very Important', 5: 'Extremely Important' },
        'likelihood': { 1: 'Very Unlikely', 2: 'Unlikely', 3: 'Neutral', 4: 'Likely', 5: 'Very Likely' }
      };
      
      return formats[question.answerFormat]?.[value] || value;
    }
    
    // Default agreement scale
    return {
      1: 'Strongly Disagree',
      2: 'Disagree',
      3: 'Neutral',
      4: 'Agree',
      5: 'Strongly Agree'
    }[value] || 'Not Answered';
  };

  // Load factor answers
  const loadFactorAnswers = async (factorNumber) => {
    try {
      const partner1Id = couple.partner1Id;
      const partner2Id = couple.partner2Id;
      
      if (!partner1Id || !partner2Id) {
        throw new Error('Partner IDs missing');
      }

      const partner1Ref = doc(db, 'assessments', partner1Id);
      const partner1Doc = await getDoc(partner1Ref);
      const partner1Data = partner1Doc.exists() ? partner1Doc.data() : null;
      
      const partner2Ref = doc(db, 'assessments', partner2Id);
      const partner2Doc = await getDoc(partner2Ref);
      const partner2Data = partner2Doc.exists() ? partner2Doc.data() : null;

      const factorQuestions = FACTOR_DATA[factorNumber]?.questions || [];

      const partner1Answers = {};
      const partner2Answers = {};

      factorQuestions.forEach(question => {
        const questionId = question.id;
        
        const p1Answer = partner1Data?.answers?.[questionId] || 
                        partner1Data?.answers?.[String(questionId)] ||
                        partner1Data?.[`answers.${questionId}`];
        
        const p2Answer = partner2Data?.answers?.[questionId] || 
                        partner2Data?.answers?.[String(questionId)] ||
                        partner2Data?.[`answers.${questionId}`];
        
        partner1Answers[questionId] = p1Answer;
        partner2Answers[questionId] = p2Answer;
      });

      setFactorAnswers(prev => ({
        ...prev,
        [factorNumber]: {
          partner1: partner1Answers,
          partner2: partner2Answers
        }
      }));

    } catch (error) {
      console.error('Error loading factor answers:', error);
      setMessage('Error loading answers. Please try again.');
    }
  };

  // Toggle expand/collapse
  const toggleExpand = async (factorNumber) => {
    if (expandedFactor === factorNumber) {
      setExpandedFactor(null);
    } else {
      setExpandedFactor(factorNumber);
      if (!factorAnswers[factorNumber]) {
        await loadFactorAnswers(factorNumber);
      }
    }
  };

  // Open factor detail modal
  const openFactorModal = async (factorNumber) => {
    setSelectedFactor(factorNumber);
    setActiveTab('questions');
    setCurrentSlide(0);
    
    if (!factorAnswers[factorNumber]) {
      await loadFactorAnswers(factorNumber);
    }
  };

  // FIXED: Unlock factor with better error handling and logging
  const handleUnlock = async (factorNumber) => {
    setLoading(prev => ({ ...prev, [factorNumber]: true }));
    setMessage('');

    try {
      const docId = getDocumentId();
      
      if (!docId) {
        throw new Error('Couple document ID not found. Please refresh the page.');
      }
      
      console.log(`🔓 Unlocking Factor ${factorNumber} for couple: ${docId}`);
      
      const coupleRef = doc(db, 'couples', docId);
      
      // First verify the document exists
      const coupleDoc = await getDoc(coupleRef);
      if (!coupleDoc.exists()) {
        throw new Error(`Couple document ${docId} not found in database`);
      }
      
      await updateDoc(coupleRef, {
        [`factorPermissions.${factorNumber}.canTake`]: true,
        [`factorPermissions.${factorNumber}.unlockedAt`]: serverTimestamp()
      });

      console.log(`✅ Factor ${factorNumber} unlocked successfully!`);
      setMessage(`✅ Factor ${factorNumber} unlocked successfully!`);
      setTimeout(() => setMessage(''), 3000);
      
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('❌ Error unlocking factor:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [factorNumber]: false }));
    }
  };

  // FIXED: Lock factor with better error handling
  const handleLock = async (factorNumber) => {
    setLoading(prev => ({ ...prev, [factorNumber]: true }));
    setMessage('');

    try {
      const docId = getDocumentId();
      
      if (!docId) {
        throw new Error('Couple document ID not found. Please refresh the page.');
      }
      
      console.log(`🔒 Locking Factor ${factorNumber} for couple: ${docId}`);
      
      const coupleRef = doc(db, 'couples', docId);
      await updateDoc(coupleRef, {
        [`factorPermissions.${factorNumber}.canTake`]: false
      });

      console.log(`✅ Factor ${factorNumber} locked successfully!`);
      setMessage(`🔒 Factor ${factorNumber} locked successfully!`);
      setTimeout(() => setMessage(''), 3000);
      
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('❌ Error locking factor:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [factorNumber]: false }));
    }
  };

  // Reveal results
  const handleReveal = async (factorNumber) => {
    setLoading(prev => ({ ...prev, [factorNumber]: true }));
    setMessage('');

    try {
      const docId = getDocumentId();
      if (!docId) throw new Error('Couple ID not found');
      
      const coupleRef = doc(db, 'couples', docId);
      await updateDoc(coupleRef, {
        [`factorPermissions.${factorNumber}.resultsRevealed`]: true,
        [`factorPermissions.${factorNumber}.revealedAt`]: serverTimestamp()
      });

      setMessage(`Factor ${factorNumber} results revealed to couple!`);
      setTimeout(() => setMessage(''), 3000);
      
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error revealing results:', error);
      setMessage('Error revealing results. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [factorNumber]: false }));
    }
  };

  // Hide results
  const handleHide = async (factorNumber) => {
    setLoading(prev => ({ ...prev, [factorNumber]: true }));
    setMessage('');

    try {
      const docId = getDocumentId();
      if (!docId) throw new Error('Couple ID not found');
      
      const coupleRef = doc(db, 'couples', docId);
      await updateDoc(coupleRef, {
        [`factorPermissions.${factorNumber}.resultsRevealed`]: false
      });

      setMessage(`Factor ${factorNumber} results hidden from couple!`);
      setTimeout(() => setMessage(''), 3000);
      
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error hiding results:', error);
      setMessage('Error hiding results. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [factorNumber]: false }));
    }
  };

  // Lock teaching
  const handleLockTeaching = async (factorNumber) => {
    setLoading(prev => ({ ...prev, [factorNumber]: true }));
    setMessage('');

    try {
      const docId = getDocumentId();
      if (!docId) throw new Error('Couple ID not found');
      
      const coupleRef = doc(db, 'couples', docId);
      await updateDoc(coupleRef, {
        [`factorPermissions.${factorNumber}.teachingVisible`]: false
      });

      setMessage(`Factor ${factorNumber} teaching locked!`);
      setTimeout(() => setMessage(''), 3000);
      
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error locking teaching:', error);
      setMessage('Error locking teaching. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [factorNumber]: false }));
    }
  };

  // Unlock teaching
  const handleUnlockTeaching = async (factorNumber) => {
    setLoading(prev => ({ ...prev, [factorNumber]: true }));
    setMessage('');

    try {
      const docId = getDocumentId();
      if (!docId) throw new Error('Couple ID not found');
      
      const coupleRef = doc(db, 'couples', docId);
      await updateDoc(coupleRef, {
        [`factorPermissions.${factorNumber}.teachingVisible`]: true
      });

      setMessage(`Factor ${factorNumber} teaching unlocked!`);
      setTimeout(() => setMessage(''), 3000);
      
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error unlocking teaching:', error);
      setMessage('Error unlocking teaching. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [factorNumber]: false }));
    }
  };

  // Lock prayer
  const handleLockPrayer = async (factorNumber) => {
    setLoading(prev => ({ ...prev, [factorNumber]: true }));
    setMessage('');

    try {
      const docId = getDocumentId();
      if (!docId) throw new Error('Couple ID not found');
      
      const coupleRef = doc(db, 'couples', docId);
      await updateDoc(coupleRef, {
        [`factorPermissions.${factorNumber}.prayerVisible`]: false
      });

      setMessage(`Factor ${factorNumber} prayer locked!`);
      setTimeout(() => setMessage(''), 3000);
      
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error locking prayer:', error);
      setMessage('Error locking prayer. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [factorNumber]: false }));
    }
  };

  // Unlock prayer
  const handleUnlockPrayer = async (factorNumber) => {
    setLoading(prev => ({ ...prev, [factorNumber]: true }));
    setMessage('');

    try {
      const docId = getDocumentId();
      if (!docId) throw new Error('Couple ID not found');
      
      const coupleRef = doc(db, 'couples', docId);
      await updateDoc(coupleRef, {
        [`factorPermissions.${factorNumber}.prayerVisible`]: true
      });

      setMessage(`Factor ${factorNumber} prayer unlocked!`);
      setTimeout(() => setMessage(''), 3000);
      
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error unlocking prayer:', error);
      setMessage('Error unlocking prayer. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [factorNumber]: false }));
    }
  };

  // Open teaching presentation
  const handleOpenTeaching = (factorNumber) => {
    setSelectedFactorForPresentation(factorNumber);
    setShowTeaching(true);
  };

  // Open prayer presentation
  const handleOpenPrayer = (factorNumber) => {
    setSelectedFactorForPresentation(factorNumber);
    setShowPrayer(true);
  };

  // Get status helpers
  const getStatusColor = (factorNumber, permission) => {
    const partner1Completed = partner1Progress?.completedFactors?.includes(factorNumber);
    const partner2Completed = partner2Progress?.completedFactors?.includes(factorNumber);
    const bothCompleted = partner1Completed && partner2Completed;

    if (permission.resultsRevealed) return 'bg-purple-100 text-purple-700 border border-purple-300';
    if (bothCompleted) return 'bg-yellow-100 text-yellow-700 border border-yellow-300';
    if (permission.canTake) return 'bg-green-100 text-green-700 border border-green-300';
    return 'bg-gray-100 text-gray-600 border border-gray-300';
  };

  const getStatusText = (factorNumber, permission) => {
    const partner1Completed = partner1Progress?.completedFactors?.includes(factorNumber);
    const partner2Completed = partner2Progress?.completedFactors?.includes(factorNumber);
    const bothCompleted = partner1Completed && partner2Completed;

    if (permission.resultsRevealed) return '🟣 Results Revealed';
    if (bothCompleted) return '🟡 Completed - Ready to Review';
    if (permission.canTake) return '🟢 Unlocked';
    return '🔒 Locked';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Factor Management for {couple.partner1Name} & {couple.partner2Name}
      </h2>

      {/* Success/Error Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('Error') 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {message}
        </div>
      )}

      {/* Factor Cards */}
      <div className="space-y-4">
        {Object.entries(FACTOR_DATA).map(([factorNum, factor]) => {
          const factorNumber = parseInt(factorNum);
          const permission = couple.factorPermissions?.[factorNumber] || {};
          
          const partner1Completed = partner1Progress?.completedFactors?.includes(factorNumber);
          const partner2Completed = partner2Progress?.completedFactors?.includes(factorNumber);
          const bothCompleted = partner1Completed && partner2Completed;

          const partner1Score = partner1Progress?.factorScores?.[factorNumber] || 0;
          const partner2Score = partner2Progress?.factorScores?.[factorNumber] || 0;

          return (
            <div key={factorNumber} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Factor {factorNumber}: {factor.name}
                  </h3>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(factorNumber, permission)}`}>
                    {getStatusText(factorNumber, permission)}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mb-4">
                {/* Preview Questions Button */}
                <button
                  onClick={() => openFactorModal(factorNumber)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition font-semibold"
                >
                  <FileText className="w-4 h-4" />
                  Preview Questions
                </button>

                {/* Unlock/Lock */}
                <button
                  onClick={() => permission.canTake ? handleLock(factorNumber) : handleUnlock(factorNumber)}
                  disabled={loading[factorNumber]}
                  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition ${
                    permission.canTake 
                      ? 'bg-orange-500 hover:bg-orange-600' 
                      : 'bg-green-500 hover:bg-green-600'
                  }`}
                >
                  {loading[factorNumber] ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : permission.canTake ? (
                    <>
                      <Lock className="w-4 h-4" />
                      Lock Factor
                    </>
                  ) : (
                    <>
                      <Unlock className="w-4 h-4" />
                      Unlock Factor
                    </>
                  )}
                </button>

                {/* View Teaching */}
                <button
                  onClick={() => handleOpenTeaching(factorNumber)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
                >
                  <Book className="w-4 h-4" />
                  View Teaching
                </button>

                {/* View Prayer */}
                <button
                  onClick={() => handleOpenPrayer(factorNumber)}
                  className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition"
                >
                  <Heart className="w-4 h-4" />
                  View Prayer
                </button>

                {/* Lock/Unlock Teaching */}
                <button
                  onClick={() => permission.teachingVisible !== false ? handleLockTeaching(factorNumber) : handleUnlockTeaching(factorNumber)}
                  disabled={loading[factorNumber]}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
                    permission.teachingVisible !== false
                      ? 'bg-gray-500 hover:bg-gray-600'
                      : 'bg-blue-500 hover:bg-blue-600'
                  } text-white disabled:bg-gray-300`}
                >
                  <Book className="w-4 h-4" />
                  {permission.teachingVisible !== false ? 'Lock Teaching' : 'Unlock Teaching'}
                </button>

                {/* Lock/Unlock Prayer */}
                <button
                  onClick={() => permission.prayerVisible !== false ? handleLockPrayer(factorNumber) : handleUnlockPrayer(factorNumber)}
                  disabled={loading[factorNumber]}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
                    permission.prayerVisible !== false
                      ? 'bg-gray-500 hover:bg-gray-600'
                      : 'bg-purple-500 hover:bg-purple-600'
                  } text-white disabled:bg-gray-300`}
                >
                  <Heart className="w-4 h-4" />
                  {permission.prayerVisible !== false ? 'Lock Prayer' : 'Unlock Prayer'}
                </button>
              </div>

              {/* Reveal/Hide Results - Only show when both completed */}
              {bothCompleted && (
                <div className="mb-4">
                  <button
                    onClick={() => permission.resultsRevealed ? handleHide(factorNumber) : handleReveal(factorNumber)}
                    disabled={loading[factorNumber]}
                    className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition ${
                      permission.resultsRevealed
                        ? 'bg-orange-500 hover:bg-orange-600'
                        : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                  >
                    {permission.resultsRevealed ? (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Hide Results from Couple
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Reveal Results to Couple
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Progress Indicators */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-sm text-gray-600">{couple.partner1Name}:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      partner1Completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {partner1Completed ? `✓ Completed (${partner1Score}/50)` : 'Not Started'}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">{couple.partner2Name}:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      partner2Completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {partner2Completed ? `✓ Completed (${partner2Score}/50)` : 'Not Started'}
                    </span>
                  </div>
                </div>
              </div>

              {/* View Individual Answers */}
              {bothCompleted && (
                <button
                  onClick={() => toggleExpand(factorNumber)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                >
                  {expandedFactor === factorNumber ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Hide Individual Answers
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      View Individual Answers
                    </>
                  )}
                </button>
              )}

              {/* Expanded Answers Section */}
              {expandedFactor === factorNumber && (
                <div className="mt-4 p-3 md:p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-bold text-base md:text-lg mb-3 md:mb-4 text-gray-800">
                    Individual Question Responses
                  </h4>
                  
                  {factorAnswers[factorNumber] ? (
                    <div className="space-y-3 md:space-y-4">
                      {factor.questions.map((question, idx) => {
                        const p1Answer = factorAnswers[factorNumber].partner1?.[question.id];
                        const p2Answer = factorAnswers[factorNumber].partner2?.[question.id];
                        
                        return (
                          <div key={question.id} className="pb-3 md:pb-4 border-b border-gray-200 last:border-0">
                            <p className="font-medium text-sm md:text-base text-gray-700 mb-2 md:mb-3">
                              Q{idx + 1}: {question.text}
                            </p>
                            
                            {question.insight && (
                              <div className="mb-3 p-2 md:p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                                <p className="text-xs md:text-sm text-blue-900">
                                  <span className="font-semibold">💡 Insight:</span> {question.insight}
                                </p>
                              </div>
                            )}
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-0 md:ml-4">
                              <div>
                                <span className="text-xs md:text-sm text-gray-600 font-medium">
                                  {couple.partner1Name}:
                                </span>
                                <div className="mt-1">
                                  <span className={`inline-block px-2 md:px-3 py-1 rounded font-medium text-xs md:text-sm ${
                                    p1Answer 
                                      ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                      : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {p1Answer ? (
                                      <span className="flex items-center gap-1">
                                        <span className="font-bold text-sm md:text-base">{p1Answer}</span>
                                        <span className="hidden sm:inline mx-1">-</span>
                                        <span className="text-xs md:text-sm">{getAnswerLabel(p1Answer, question)}</span>
                                      </span>
                                    ) : (
                                      'Not Answered'
                                    )}
                                  </span>
                                </div>
                              </div>
                              
                              <div>
                                <span className="text-xs md:text-sm text-gray-600 font-medium">
                                  {couple.partner2Name}:
                                </span>
                                <div className="mt-1">
                                  <span className={`inline-block px-2 md:px-3 py-1 rounded font-medium text-xs md:text-sm ${
                                    p2Answer 
                                      ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                                      : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {p2Answer ? (
                                      <span className="flex items-center gap-1">
                                        <span className="font-bold text-sm md:text-base">{p2Answer}</span>
                                        <span className="hidden sm:inline mx-1">-</span>
                                        <span className="text-xs md:text-sm">{getAnswerLabel(p2Answer, question)}</span>
                                      </span>
                                    ) : (
                                      'Not Answered'
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 md:py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
                      <p className="text-sm md:text-base text-gray-500">Loading answers...</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Factor Detail Modal */}
      {selectedFactor && (
        <FactorDetailModal
          factorNumber={selectedFactor}
          factor={FACTOR_DATA[selectedFactor]}
          couple={couple}
          factorAnswers={factorAnswers[selectedFactor]}
          permission={couple.factorPermissions?.[selectedFactor] || {}}
          partner1Completed={partner1Progress?.completedFactors?.includes(selectedFactor)}
          partner2Completed={partner2Progress?.completedFactors?.includes(selectedFactor)}
          partner1Score={partner1Progress?.factorScores?.[selectedFactor] || 0}
          partner2Score={partner2Progress?.factorScores?.[selectedFactor] || 0}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          onClose={() => setSelectedFactor(null)}
          onUnlock={handleUnlock}
          onLock={handleLock}
          onReveal={handleReveal}
          onHide={handleHide}
          loading={loading[selectedFactor]}
          getAnswerLabel={getAnswerLabel}
        />
      )}

      {/* Teaching Presentation Modal */}
      {showTeaching && (
        <TeachingPresentation
          factorNumber={selectedFactorForPresentation}
          onClose={() => setShowTeaching(false)}
        />
      )}

      {/* Prayer Presentation Modal */}
      {showPrayer && (
        <PrayerPresentation
          factorNumber={selectedFactorForPresentation}
          onClose={() => setShowPrayer(false)}
        />
      )}
    </div>
  );
};

/**
 * Factor Detail Modal Component
 */
const FactorDetailModal = ({
  factorNumber,
  factor,
  couple,
  factorAnswers,
  permission,
  partner1Completed,
  partner2Completed,
  partner1Score,
  partner2Score,
  activeTab,
  setActiveTab,
  currentSlide,
  setCurrentSlide,
  onClose,
  onUnlock,
  onLock,
  onReveal,
  onHide,
  loading,
  getAnswerLabel
}) => {
  const bothCompleted = partner1Completed && partner2Completed;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-3xl font-bold mb-2">
                Factor {factorNumber}: {factor?.name}
              </h2>
              <p className="text-purple-100">
                {couple.partner1Name} & {couple.partner2Name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setActiveTab('questions')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                activeTab === 'questions'
                  ? 'bg-white text-purple-600'
                  : 'bg-purple-500 bg-opacity-50 text-white hover:bg-opacity-70'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span className="hidden sm:inline">Questions (10)</span>
              <span className="sm:hidden">Questions</span>
            </button>
            <button
              onClick={() => setActiveTab('teaching')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                activeTab === 'teaching'
                  ? 'bg-white text-purple-600'
                  : 'bg-purple-500 bg-opacity-50 text-white hover:bg-opacity-70'
              }`}
            >
              <Book className="w-5 h-5" />
              <span className="hidden sm:inline">Teaching</span>
              <span className="sm:hidden">Teach</span>
            </button>
            <button
              onClick={() => setActiveTab('prayer')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                activeTab === 'prayer'
                  ? 'bg-white text-purple-600'
                  : 'bg-purple-500 bg-opacity-50 text-white hover:bg-opacity-70'
              }`}
            >
              <Heart className="w-5 h-5" />
              <span className="hidden sm:inline">Prayer</span>
              <span className="sm:hidden">Pray</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'questions' && (
            <QuestionsTab
              factor={factor}
              couple={couple}
              factorAnswers={factorAnswers}
              partner1Score={partner1Score}
              partner2Score={partner2Score}
              bothCompleted={bothCompleted}
              getAnswerLabel={getAnswerLabel}
            />
          )}
          
          {activeTab === 'teaching' && (
            <TeachingTab
              factorNumber={factorNumber}
              currentSlide={currentSlide}
              setCurrentSlide={setCurrentSlide}
            />
          )}
          
          {activeTab === 'prayer' && (
            <PrayerTab factorNumber={factorNumber} />
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t p-6 bg-gray-50 flex-shrink-0">
          <div className="flex flex-wrap gap-3">
            {!permission.canTake ? (
              <button
                onClick={() => onUnlock(factorNumber)}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold disabled:opacity-50 transition"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Unlock className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">Unlock Factor for Couple</span>
                <span className="sm:hidden">Unlock</span>
              </button>
            ) : (
              <button
                onClick={() => onLock(factorNumber)}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold disabled:opacity-50 transition"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Lock className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">Lock Factor</span>
                <span className="sm:hidden">Lock</span>
              </button>
            )}

            {bothCompleted && !permission.resultsRevealed && (
              <button
                onClick={() => onReveal(factorNumber)}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold disabled:opacity-50 transition"
              >
                <Eye className="w-5 h-5" />
                <span className="hidden sm:inline">Reveal Results</span>
                <span className="sm:hidden">Reveal</span>
              </button>
            )}

            {permission.resultsRevealed && (
              <button
                onClick={() => onHide(factorNumber)}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold disabled:opacity-50 transition"
              >
                <EyeOff className="w-5 h-5" />
                <span className="hidden sm:inline">Hide Results</span>
                <span className="sm:hidden">Hide</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Questions Tab
 */
const QuestionsTab = ({ factor, couple, factorAnswers, partner1Score, partner2Score, bothCompleted, getAnswerLabel }) => {
  if (!factor || !factor.questions) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No questions available for this factor</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Factor Overview */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-bold text-blue-900 mb-2">📋 Factor Overview</h3>
        <p className="text-blue-800 text-sm">
          This factor assesses: <strong>{factor.name}</strong>
        </p>
        <p className="text-blue-700 text-sm mt-2">
          Couples will answer these 10 questions on a scale of 1-5. Total possible score: 50 points.
        </p>
      </div>

      {/* Score Summary (if both completed) */}
      {bothCompleted && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border-2 border-purple-200">
          <h3 className="font-bold text-lg mb-4">Factor Scores</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">{couple.partner1Name}</div>
              <div className="text-4xl font-bold text-purple-600">{partner1Score}/50</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">{couple.partner2Name}</div>
              <div className="text-4xl font-bold text-pink-600">{partner2Score}/50</div>
            </div>
          </div>
        </div>
      )}

      {/* Questions List */}
      <div className="space-y-4">
        {factor.questions.map((question, index) => {
          const p1Answer = factorAnswers?.partner1?.[question.id];
          const p2Answer = factorAnswers?.partner2?.[question.id];

          return (
            <div
              key={question.id}
              className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:border-purple-300 transition"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="font-bold text-purple-600">{index + 1}</span>
                  </div>
                </div>

                <div className="flex-1">
                  <p className="font-semibold text-lg text-gray-800 mb-2">
                    {question.text}
                  </p>

                  {question.insight && (
                    <div className="bg-gray-50 border-l-4 border-gray-300 p-3 mb-3">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">💡 Insight:</span> {question.insight}
                      </p>
                    </div>
                  )}

                  {/* Answer Scale - Dynamic based on question format */}
                  <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-3">
                    {(question.options && Array.isArray(question.options)) ? (
                      // Custom options
                      question.options.map((opt, idx) => {
                        const colors = ['bg-red-100', 'bg-orange-100', 'bg-yellow-100', 'bg-lime-100', 'bg-green-100'];
                        return (
                          <span key={opt.value} className={`px-2 py-1 ${colors[idx] || 'bg-gray-100'} rounded`}>
                            {opt.value} - {opt.text}
                          </span>
                        );
                      })
                    ) : (
                      // Default agreement scale
                      <>
                        <span className="px-2 py-1 bg-red-100 rounded">1 - Strongly Disagree</span>
                        <span className="px-2 py-1 bg-orange-100 rounded">2 - Disagree</span>
                        <span className="px-2 py-1 bg-yellow-100 rounded">3 - Neutral</span>
                        <span className="px-2 py-1 bg-lime-100 rounded">4 - Agree</span>
                        <span className="px-2 py-1 bg-green-100 rounded">5 - Strongly Agree</span>
                      </>
                    )}
                  </div>

                  {/* Partner Answers (if completed) */}
                  {(p1Answer || p2Answer) && (
                    <div className="flex flex-col sm:flex-row gap-4 mt-3 pt-3 border-t">
                      <div className="flex-1">
                        <div className="text-xs text-gray-600 mb-1">{couple.partner1Name}</div>
                        <div className="text-2xl font-bold text-purple-600">
                          {p1Answer ? `${p1Answer}/5` : '—'}
                        </div>
                        {p1Answer && (
                          <div className="text-xs text-gray-600 mt-1">
                            {getAnswerLabel(p1Answer, question)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-xs text-gray-600 mb-1">{couple.partner2Name}</div>
                        <div className="text-2xl font-bold text-pink-600">
                          {p2Answer ? `${p2Answer}/5` : '—'}
                        </div>
                        {p2Answer && (
                          <div className="text-xs text-gray-600 mt-1">
                            {getAnswerLabel(p2Answer, question)}
                          </div>
                        )}
                      </div>
                      {p1Answer && p2Answer && (
                        <div className="flex-1">
                          <div className="text-xs text-gray-600 mb-1">Difference</div>
                          <div className={`text-2xl font-bold ${
                            Math.abs(p1Answer - p2Answer) > 2 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {Math.abs(p1Answer - p2Answer)}
                          </div>
                          {Math.abs(p1Answer - p2Answer) > 2 && (
                            <div className="text-xs text-orange-600 mt-1">⚠️ Discuss</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Teaching Tab
 */
const TeachingTab = ({ factorNumber, currentSlide, setCurrentSlide }) => {
  const { TEACHING_CONTENT } = require('../data/teachingContent');
  
  const factorKey = `factor${factorNumber}`;
  const teachingData = TEACHING_CONTENT[factorKey];

  if (!teachingData || !teachingData.slides) {
    return (
      <div className="text-center py-12">
        <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No teaching content available for this factor</p>
      </div>
    );
  }

  const slides = teachingData.slides;
  const currentSlideData = slides[currentSlide];

  return (
    <div className="space-y-6">
      <div className="text-center text-gray-600 font-semibold">
        Slide {currentSlide + 1} of {slides.length}
      </div>

      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 min-h-[400px] flex flex-col justify-center border-2 border-purple-200">
        {currentSlideData.title && (
          <h2 className="text-3xl font-bold text-center text-purple-900 mb-6">
            {currentSlideData.title}
          </h2>
        )}

        <div className="text-lg text-gray-800 leading-relaxed">
          {currentSlideData.content.split('\n').map((line, i) => (
            <p key={i} className="mb-4">{line}</p>
          ))}
        </div>

        {currentSlideData.scripture && (
          <div className="mt-6 p-4 bg-white rounded-lg border-2 border-blue-200">
            <p className="text-blue-900 font-serif italic text-center">
              "{currentSlideData.scripture}"
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        <button
          onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
          disabled={currentSlide === 0}
          className="flex items-center gap-2 px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft className="w-5 h-5" />
          Previous
        </button>

        <div className="flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition ${
                index === currentSlide ? 'bg-purple-600' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
          disabled={currentSlide === slides.length - 1}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          Next
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

/**
 * Prayer Tab
 */
const PrayerTab = ({ factorNumber }) => {
  const { PRAYER_CONTENT } = require('../data/prayerContent');
  
  const factorKey = `factor${factorNumber}`;
  const prayerData = PRAYER_CONTENT[factorKey];

  if (!prayerData || !prayerData.prayers) {
    return (
      <div className="text-center py-12">
        <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No prayer content available for this factor</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-6 border-2 border-pink-200">
        <h3 className="font-bold text-xl text-purple-900 mb-3">
          {prayerData.title}
        </h3>
        <p className="text-gray-700">
          {prayerData.introduction}
        </p>
      </div>

      <div className="space-y-4">
        {prayerData.prayers.map((prayer, index) => (
          <div
            key={index}
            className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-purple-300 transition shadow-sm"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                  <span className="font-bold text-white text-lg">{index + 1}</span>
                </div>
              </div>

              <div className="flex-1">
                <h4 className="font-bold text-lg text-gray-800 mb-3">
                  {prayer.title}
                </h4>

                <p className="text-gray-700 leading-relaxed mb-4">
                  {prayer.prayer}
                </p>

                {prayer.scripture && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                    <p className="text-blue-900 font-serif italic text-sm">
                      {prayer.scripture}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {prayerData.closing && (
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-6 border-2 border-purple-300">
          <p className="text-purple-900 font-semibold text-center">
            {prayerData.closing}
          </p>
        </div>
      )}
    </div>
  );
};

export default CounselorFactorControl;